/**
 * utils/wac.js
 * Weighted-average cost (WAC), Phase T1 PRD §4.4. Derived on demand from `data.transactions`
 * — NEVER persisted, so editing/deleting a past PEMBELIAN is trivially correct: the replay
 * just runs again over the corrected history, with no incremental-update bugs possible.
 *
 * `useAvgCosts` (below) is this directory's second sanctioned exception to the "no React" rule
 * (the first is `modalStack.js`) — a small cross-cutting hook that doesn't belong in
 * `components/` or `pages/` either. It is NOT a cache: no module-level state, no cross-
 * component sharing. It is the `useMemo(() => computeAvgCosts(transactions, expenses),
 * [transactions, expenses])` each of the 3 real call sites (`DetailBarangModal.js`, `Penjualan.js`,
 * `PiutangUtang.js`) used to write independently — defined once so it can't silently drift
 * between them, with each call site still getting its own React memo cell scoped to its own
 * component instance, still recomputing only when its own `transactions` reference changes.
 * This deliberately does NOT introduce anything that could outlive a single render/mount —
 * doing so would risk reintroducing exactly the staleness the "never persisted" design above
 * already avoids.
 *
 * **Purchase costs go into the items' cost ("landed cost", confirmed 2026-10-11).** Following
 * PSAK 14 (cost of inventory = everything it took to get the goods in, minus discounts), a
 * Pembelian's own extras — its Tagihan rows (stored type "tagihanSupplier", +), Potongan rows
 * (−), any leftover legacy "biaya" row (+), and every Biaya Lain-Lain linked to it (+, e.g. a
 * truck, bongkar muat or a broker) — are spread over its lines BY VALUE (qty × price), as the
 * client chose, and the same way Zoho/Odoo/QuickBooks allocate landed costs. Example: items worth 600.000 and 400.000 with a 100.000 truck → their
 * unit cost rises by 60.000/qty and 40.000/qty. A purchase whose goods are worth 0 spreads by
 * quantity instead; with no quantity either, there's nothing to spread onto. Nothing is rounded
 * or stored here (display rounds to the thousand), so the shares always add back exactly.
 * Talangan never appears on a Pembelian. Costs on a Penjualan/Transfer never touch item cost.
 */
import { useMemo } from "react";
import { sumBiayaCostItems, sumPotonganCostItems } from "./costItems";

/** Chronological replay order: (date, createdAt) ascending. */
function sortedForReplay(transactions) {
  return [...(transactions || [])].sort((a, b) => {
    if (a.date !== b.date) return a.date < b.date ? -1 : 1;
    if (a.createdAt !== b.createdAt) return a.createdAt < b.createdAt ? -1 : 1;
    return 0;
  });
}

/**
 * Replay every PEMBELIAN (and PENJUALAN/TRANSFER for their unit-count effect only) in
 * (date, createdAt) order, per item.
 *   - First purchase, or a purchase when running units <= 0: avg resets to that purchase's
 *     unitPrice (the costed pool was empty/negative, so there's nothing to blend with).
 *   - Otherwise: avg = (avg*units + qty*unitPrice) / (units+qty).
 *   - PENJUALAN and TRANSFER reduce units but never move avg — sales don't cost anything,
 *     transfers are bucket-blind and internal.
 * A code with no PEMBELIAN history at all never enters the map — callers should treat a
 * missing key the same as an explicit null ("—").
 *
 * @returns {Map<string, number|null>} internalCode -> avgCost
 */
/** The purchase's own extras that belong in item cost — see the file header. */
function purchaseExtras(txn, expenseByTxn) {
  const items = txn.costItems || [];
  const tagihanSupplier = items.reduce((s, c) => s + (c.type === "tagihanSupplier" ? c.amount : 0), 0);
  return tagihanSupplier - sumPotonganCostItems(items) + sumBiayaCostItems(items) + (expenseByTxn.get(txn.id) || 0);
}

/** Effective unit cost of each line after spreading `extra` by value (fallback: by qty). */
export function landedUnitCosts(lines, extra) {
  const ls = lines || [];
  const value = ls.reduce((s, l) => s + l.qty * l.unitPrice, 0);
  const qty = ls.reduce((s, l) => s + l.qty, 0);
  return ls.map((l) => {
    if (!extra || l.qty <= 0) return l.unitPrice;
    const share = value > 0 ? (extra * (l.qty * l.unitPrice)) / value : qty > 0 ? (extra * l.qty) / qty : 0;
    return l.unitPrice + share / l.qty;
  });
}

export function computeAvgCosts(transactions, expenses = []) {
  const state = new Map(); // internalCode -> { units, avg }
  const expenseByTxn = new Map();
  for (const e of expenses || []) {
    if (e.txnId) expenseByTxn.set(e.txnId, (expenseByTxn.get(e.txnId) || 0) + (Number(e.amount) || 0));
  }

  for (const txn of sortedForReplay(transactions)) {
    if (txn.type === "PEMBELIAN") {
      const unitCosts = landedUnitCosts(txn.lines, purchaseExtras(txn, expenseByTxn));
      (txn.lines || []).forEach((line, i) => {
        const unitCost = unitCosts[i];
        const cur = state.get(line.internalCode);
        if (!cur || cur.units <= 0) {
          state.set(line.internalCode, { units: line.qty, avg: unitCost });
        } else {
          const units = cur.units + line.qty;
          const avg = (cur.avg * cur.units + line.qty * unitCost) / units;
          state.set(line.internalCode, { units, avg });
        }
      });
    } else if (txn.type === "PENJUALAN" || txn.type === "TRANSFER") {
      for (const line of txn.lines || []) {
        const cur = state.get(line.internalCode);
        if (!cur) continue; // no costed pool exists yet — nothing to reduce
        state.set(line.internalCode, { units: cur.units - line.qty, avg: cur.avg });
      }
    }
  }

  const result = new Map();
  for (const [code, { avg }] of state) result.set(code, avg);
  return result;
}

/**
 * Shared per-render memoization wrapper around `computeAvgCosts` — see this file's own header
 * comment for why this hook lives here rather than in `components/`/`pages/`. Each caller gets
 * its own independent memo cell; there is no sharing between call sites.
 */
export function useAvgCosts(transactions, expenses) {
  return useMemo(() => computeAvgCosts(transactions, expenses), [transactions, expenses]);
}

/**
 * Format a WAC value rounded to the nearest THOUSAND Rupiah, Indonesian-style ("." thousands) —
 * e.g. 34920.634 -> "35.000". "—" for null/undefined.
 *
 * **3rd-round fix, a further contract change from the 2nd round's nearest-whole-Rupiah
 * rounding** (that round's own comment, describing THAT change from the original
 * truncate-to-3-decimals design, is kept below for historical context — this round only
 * changes the rounding GRANULARITY, not which value gets rounded or where). Confirmed via a
 * real example that the actual requirement is nearest thousand, not nearest rupiah: a raw WAC
 * of 34.920,634 should read "Rp 35.000", not "Rp 34.921". `computeAvgCosts` stays completely
 * untouched, same as the 2nd round — rounding lives HERE (display time) only.
 */
export function formatWac(value) {
  if (value === null || value === undefined) return "—";
  return (Math.round(value / 1000) * 1000).toLocaleString("id-ID");
}
